import React from "react";
import logo  from "../assets/logo.svg";
import right from "../assets/right.svg";
import { FOOTER } from "../data/constants";

interface DesktopFooterProps {
  onOpenEnquireModal: () => void;
  footerInView?: boolean;
}

const FOOTER_HEIGHT = 560; // keep in sync with layout-constants.ts

const buttonClass = [
  // layout & sizing
  "inline-flex items-center justify-center",
  "w-[131px] h-[52px]",
  "px-8 pt-[15px] pb-[15px] gap-[12px]",
  // base styling
  "bg-white text-black font-semibold shadow-md overflow-hidden",
  // transition
  "transition-all duration-100",
  // default shape
  "[clip-path:polygon(0_0,100%_0,100%_100%,0_100%)]",
  // hover state: colours + bottom-right bevel
  "hover:bg-[#46C1BF] hover:text-black",
  "hover:[clip-path:polygon(0_0,100%_0,100%_calc(100%_-_16px),calc(100%_-_16px)_100%,0_100%)]",
].join(" ");

const DesktopFooter: React.FC<DesktopFooterProps> = ({ onOpenEnquireModal, footerInView }) => {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer
      className={`
        fixed bottom-0 inset-x-0 z-0
        h-[${FOOTER_HEIGHT}px] bg-cover bg-center text-white
        pt-[120px] pb-[46px] px-6 md:px-16
      `}
      style={{ backgroundImage: "url('/backdrop.png')" }}
    >
      {/* ───────────── columns ───────────── */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
        {/* Left */}
        <div>
          <img src={logo} alt="SKTuff Logo" className="mb-8 w-[167px]" />
          <p className="font-inter text-[18px] leading-relaxed max-w-xs mb-8">
            {FOOTER.companyDescription}
          </p>
          <button className={buttonClass} onClick={onOpenEnquireModal}>{FOOTER.exploreButton}</button>
          <img src={right} alt="" className="mt-[101px]" />
        </div>

        {/* Middle */}
        <div>
          <h4 className="font-inter font-medium text-[18px] leading-[16px] mb-6 inline-block relative">
            {FOOTER.sitemapHeading}
            <span className={`absolute left-0 top-full mt-[8px] block h-[2px] bg-white transition-all duration-1000 ${footerInView ? 'w-[406px]' : 'w-0'}`} />
          </h4>
          <ul className="mt-3 space-y-2">
            {FOOTER.sitemap.map((item, idx) => (
              <li key={idx}>
                <a
                  href={item.href}
                  className="hover:text-[#46C1BF] font-inter font-normal text-[18px] leading-[16px]"
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Right */}
        <div>
          <h4 className="font-inter font-medium text-[18px] leading-[16px] mb-6 inline-block relative">
            {FOOTER.contact.heading}
            <span className={`absolute left-0 top-full mt-[8px] block h-[2px] bg-white transition-all duration-1000 ${footerInView ? 'w-[406px]' : 'w-0'}`} />
          </h4>
          <p
            className="font-inter text-[18px] leading-[150%] mt-3 whitespace-pre-line"
          >
            {FOOTER.contact.address}
          </p>
          <p className="font-inter text-[18px] leading-[150%] mt-5">
            {FOOTER.contact.phone}
          </p>
          <p className="font-inter text-[18px] leading-[150%]">
            <a
              href={`mailto:${FOOTER.contact.email}`}
              className="font-inter text-[18px] leading-[150%]  hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {FOOTER.contact.email}
            </a>
          </p>
        </div>
      </div>

      {/* Back-to-top */}
      <div className="absolute bottom-[36px] right-[79px] md:right-16">
        <button
          onClick={scrollToTop}
          className="group flex flex-col items-start text-white"
          type="button"
        >
          <span className="flex items-center space-x-1">
            <span className="font-inter text-[18px] font-medium">Back To Top</span>
            <span className="text-xl">↑</span>
          </span>
          <span
            className={`block h-[2px] bg-white mt-1 transition-all duration-1000 ${footerInView ? 'w-full' : 'w-0'}`}
          />
        </button>
      </div>
    </footer>
  );
};

export default DesktopFooter;
